/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NumberInput } from 'carbon-addons-iot-react';
import { useField } from 'formik';
import isNil from 'lodash/isNil';
import isNaN from 'lodash/isNaN';
import { useDebounce } from 'use-debounce';
import { defineMessages, injectIntl } from 'react-intl';

import { TextValueFormik, FormLabelTooltip } from 'Common';

const messages = defineMessages({
  required: 'Required',
});

const propTypes = {
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  hideLabel: PropTypes.bool,
  syncToFormikDebounceMillis: PropTypes.number,
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'xl']),
  /**
   * When true, if form input is disabled it will render as if it were
   * just a normal (potentially labelled) text value and not as a disabled input.
   * This pattern (input when enabled, plaintext when disabled) is used repeatedly
   * throughout the connect design.
   */
  renderAsTextWhenDisabled: PropTypes.bool,
  /** is this field required */
  required: PropTypes.bool,
  /** show tooltip for field */
  tooltip: PropTypes.string,
  /** detailed documentation link */
  learnMore: PropTypes.string,
};

const defaultProps = {
  disabled: false,
  label: '',
  hideLabel: false,
  syncToFormikDebounceMillis: 500,
  step: 1,
  min: undefined,
  max: undefined,
  size: undefined,
  renderAsTextWhenDisabled: true,
  required: false,
  tooltip: null,
  learnMore: null,
};

const NumberInputFormik = ({
  id,
  disabled,
  renderAsTextWhenDisabled,
  label,
  hideLabel,
  syncToFormikDebounceMillis,
  step,
  min,
  max,
  size,
  required,
  tooltip,
  learnMore,
  intl: { formatMessage },
  ...others
}) => {
  // If there is a tool tip defined on the label then use FormLabelTooltip component.
  const labelComponent = tooltip ? (
    <FormLabelTooltip label={label} required={required} tooltip={tooltip} learnMore={learnMore} />
  ) : (
    label
  );

  const [field, meta, helpers] = useField(id);

  const [value, setValue] = useState({
    // The contents of the input field
    data: field.value,

    // iff true, the value of data will be syned back to Formik state
    // once the debounce timeout expires
    syncToFormik: false,
  });

  // periodically sync changes to value back to formik state
  const [valueDebounced] = useDebounce(value, syncToFormikDebounceMillis);
  useEffect(() => {
    if (valueDebounced.syncToFormik) {
      helpers.setValue(valueDebounced.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueDebounced]);

  // sync changes made to formik model back to the rendered value
  // (this occurs e.g. when the form is reset, if the user selects a different event ID in the PI editor, if the user removes a row from the schema editor table, etc)
  useEffect(() => {
    // console.log('sync from formik -> rendered: ', field.value)
    setValue({
      data: field.value,
      // here we are syncing an update from formik to our locally-rendered value
      // don't propagate this change back to Formik when the debounce timeout expires
      syncToFormik: false,
    });
  }, [field.value]);

  return renderAsTextWhenDisabled && disabled ? (
    <TextValueFormik id={id} hideLabel={hideLabel} labelText={labelComponent} />
  ) : (
    <NumberInput
      id={id}
      name={id}
      onChange={e => {
        // in this case, the change was made by the user modifying the input field's contents
        // so we want to sync the change back to formik state
        const v = e.imaginaryTarget.valueAsNumber;
        if (!isNaN(v)) {
          setValue({
            data: v,
            syncToFormik: true,
          });
        }
      }}
      value={isNil(value.data) ? 0 : value.data} // ensure we don't put component into uncontrolled mode when value isNil
      // defaultValue={isNil(value.data) ? 0 : value.data}
      label={labelComponent}
      hideLabel={hideLabel}
      invalid={meta.error && meta.touched}
      invalidText={meta.error || formatMessage(messages.required)}
      step={step}
      disabled={disabled}
      min={min}
      max={max}
      size={size}
      allowEmpty={!required}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...others}
    />
  );
};

NumberInputFormik.propTypes = propTypes;
NumberInputFormik.defaultProps = defaultProps;

export default injectIntl(NumberInputFormik);
